import { useState } from 'react'
import { useLibraryStore } from '../../store/LibraryStore'
import RasterImage from '../ui/image'
import { useInfiniteQuery } from '@tanstack/react-query'
import { fetcher } from '../../fetcher'
import { getAssetsFromLibraryQuery } from '../../graphql/ImagesQuery'
import { useConfigStore } from '../../store/ConfigStore'
import { useImageStore } from '../../store/ImageStore'
import { Provider } from '../provider'
import { useMemo, useRef, useEffect } from 'react'
import '../../instrument'
import { useShallow } from 'zustand/shallow'

const INITIAL_PAGE = 0
const PAGE_SIZE = 500

function RasterImages({ isSingleImage }: { isSingleImage: boolean }) {
  const [isVersionsView, setIsVersionsView] = useState(false)
  const [photoViews, setPhotoViews] = useState<Image[]>([])

  const [id] = useLibraryStore(useShallow((state) => [state.id]))
  const [config] = useConfigStore(useShallow((state) => [state.config]))
  const [selected, setPhoto] = useImageStore(useShallow((state) => [state.selected, state.setPhoto]))

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['raster-assets', { libraryId: id }],
    initialPageParam: INITIAL_PAGE,
    queryFn: ({ pageParam = INITIAL_PAGE }) => {
      return fetcher(
        getAssetsFromLibraryQuery,
        {
          organizationId: config.orgId!,
          libraryId: id,
          page: pageParam,
          pageSize: PAGE_SIZE,
          tags: [],
        },
        { apiKey: config.apiKey, orgId: config.orgId },
      )
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.photos?.length || lastPage.photos.length < PAGE_SIZE) {
        return undefined
      }
      return allPages.length
    },
    enabled: !!config.orgId && !!config.apiKey && !!id,
  })

  // Combine all pages of data
  const allPhotos = useMemo(() => {
    return data?.pages.reduce<Image[]>((acc, page: { assets: Image[] }) => {
      return [...acc, ...(page?.assets || [])]
    }, [])
  }, [data?.pages])

  // Intersection Observer setup
  const observerTarget = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const showPhotoViews = (photo: Image) => {
    setIsVersionsView(true)
    if (photo.views) {
      setPhotoViews([photo, ...photo.views])
    }
  }

  const handlePhotoClick = (asset: Image) => {
    let media = asset
    // Check if it's a video (null height and width)
    const isVideo = asset.height === null && asset.width === null

    // For versions of images, get parent dimensions
    if (asset.parentId && !isVideo) {
      const parent = data?.pages[0]?.assets.find((p: Image) => p.id === asset.parentId)
      media = { ...media, width: parent?.width ?? '', height: parent?.height ?? '' }
    }

    // For all media types (images and videos)
    setPhoto({ photo: media, isSingleImage })
    setIsVersionsView(false)
  }

  if (!id) {
    return <div className="h-[500px] flex items-center justify-center">Select a library to view 👀 images</div>
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {Array(8)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="w-full h-56 animate-pulse bg-gray-200 rounded-md" />
          ))}
      </div>
    )
  }

  if (isVersionsView) {
    return (
      // Versions view for selected image
      <div className="flex flex-col gap-5 py-5">
        <button
          onClick={() => setIsVersionsView(false)}
          className="w-fit h-fit bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded font-medium transition-colors"
        >
          Go back
        </button>

        <p>The selected image has more than one version, please make a selection below.</p>

        {photoViews && (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {photoViews.map((view: Image, index: number) => {
              const isVideo = view.height === null && view.width === null
              const parent = data?.pages[0]?.assets.find((p: Image) => p.id === view.parentId)
              return (
                <RasterImage
                  key={view.id}
                  image={{
                    ...view,
                    // Only set width/height from parent for images, not for videos
                    width: isVideo ? null : (view.width ?? parent?.width),
                    height: isVideo ? null : (view.height ?? parent?.height),
                  }}
                  original={index === 0}
                  displayName
                  selected={selected?.some((selected) => selected.id === view.id)}
                  chooseImage={() => handlePhotoClick(view)}
                  version
                />
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Return the main grid with infinite scroll
  return (
    <div className="flex flex-col gap-4">
      <div className="columns-2 lg:columns-3 xl:columns-4 gap-4">
        {allPhotos?.map((photo: Image) => (
          <RasterImage
            key={photo.id}
            image={photo}
            displayName={false}
            openVersions={showPhotoViews}
            chooseImage={() => (photo.views?.length ? showPhotoViews(photo) : handlePhotoClick(photo))}
            selected={selected?.some((selected) => selected?.id === photo?.id)}
            versionSelected={photo.views?.some((view) => selected?.some((selected) => selected.id === view.id))}
          />
        ))}
      </div>

      {/* Loading indicator and observer target */}
      <div ref={observerTarget} className="h-4 w-full">
        {isFetchingNextPage && (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array(4)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="w-full h-56 animate-pulse bg-gray-200 rounded-md" />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function Images({ config, isSingleImage = false }: { config: Settings; isSingleImage?: boolean }) {
  return (
    <Provider config={config}>
      <RasterImages isSingleImage={isSingleImage} />
    </Provider>
  )
}
